import { useState, useEffect } from 'react';
import { supabase } from '@/integrations/supabase/client';
import { errorLogger } from '@/lib/error-logger';

/**
 * Hook to check if the current user is a super admin
 * 
 * SECURITY NOTE: This hook performs client-side checks for UI purposes only.
 * All super-admin-only operations MUST also verify authorization server-side.
 * 
 * For RLS policies, use the is_super_admin(_user_id uuid) security definer function
 * instead of querying super_admins directly to avoid recursive RLS issues.
 * 
 * Example RLS policy:
 * CREATE POLICY "Super admins can manage users"
 * ON public.profiles
 * FOR ALL
 * TO authenticated
 * USING (public.is_super_admin(auth.uid()));
 */
export const useSuperAdmin = () => {
  const [isSuperAdmin, setIsSuperAdmin] = useState<boolean>(false);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    checkSuperAdminStatus();
  }, []);

  const checkSuperAdminStatus = async () => {
    try {
      const { data: { user } } = await supabase.auth.getUser();
      
      if (!user) {
        setIsSuperAdmin(false);
        setLoading(false);
        return;
      }

      // Check if user is in super_admins table
      const { data, error } = await supabase
        .from('super_admins')
        .select('id')
        .eq('user_id', user.id)
        .maybeSingle();

      if (error) {
        await errorLogger.log(error, { severity: 'error' });
        setIsSuperAdmin(false);
      } else {
        setIsSuperAdmin(!!data);
      }
    } catch (error) {
      await errorLogger.log(error as Error, { severity: 'error' });
      setIsSuperAdmin(false);
    } finally {
      setLoading(false);
    }
  };

  return { isSuperAdmin, loading, refetch: checkSuperAdminStatus };
};
